/**
 * useAdminLocationAssignments Hook
 * 
 * Custom hook for fetching and managing admin location assignments.
 */

import * as React from "react";
import { adminService, type AdminLocationAssignment, type AdminLevel } from "@/services/adminService";

export interface LocationAssignmentFilters {
  admin?: string;
  level?: AdminLevel;
  isActive?: boolean;
  province?: string;
  city?: string;
}

export function useAdminLocationAssignments(filters?: LocationAssignmentFilters) {
  const [assignments, setAssignments] = React.useState<AdminLocationAssignment[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [total, setTotal] = React.useState(0);

  const fetchAssignments = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await adminService.getAllLocationAssignments(filters);
      setAssignments(result.data);
      setTotal(result.total);
    } catch (err) {
      console.error("Error fetching location assignments:", err);
      setError(err instanceof Error ? err.message : "Gagal memuat data penugasan lokasi");
      setAssignments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  React.useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  return {
    assignments,
    loading,
    error,
    total,
    fetchAssignments,
  };
}

export function useCreateLocationAssignment() {
  const [creating, setCreating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const createLocationAssignment = React.useCallback(async (input: {
    admin: string;
    level: AdminLevel;
    provinces?: string[];
    cities?: string[];
    isActive?: boolean;
    notes?: string;
  }) => {
    setCreating(true);
    setError(null);

    try {
      const assignment = await adminService.createLocationAssignment(input);
      return assignment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal membuat penugasan lokasi";
      setError(errorMessage);
      throw err;
    } finally {
      setCreating(false);
    }
  }, []);

  return {
    creating,
    error,
    createLocationAssignment,
  };
}

export function useUpdateLocationAssignment() {
  const [updating, setUpdating] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const updateLocationAssignment = React.useCallback(async (
    id: string,
    input: {
      provinces?: string[];
      cities?: string[];
      isActive?: boolean;
      notes?: string;
    }
  ) => {
    setUpdating(true);
    setError(null);

    try {
      const assignment = await adminService.updateLocationAssignment(id, input);
      return assignment;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal mengupdate penugasan lokasi";
      setError(errorMessage);
      throw err;
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    updating,
    error,
    updateLocationAssignment,
  };
}

export function useDeleteLocationAssignment() {
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const deleteLocationAssignment = React.useCallback(async (id: string) => {
    setDeleting(true);
    setError(null);

    try {
      await adminService.deleteLocationAssignment(id);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Gagal menghapus penugasan lokasi";
      setError(errorMessage);
      throw err;
    } finally {
      setDeleting(false);
    }
  }, []);

  return {
    deleting,
    error,
    deleteLocationAssignment,
  };
}

